import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dateInputToUnix } from '@/shared/lib/dateInput';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { MultiSelect } from '@/shared/ui/MultiSelect';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { EXPORT_STATUS, fetchPromoCodeOptions, hasExportResult, fetchUtmSourceOptions } from './api';
import { ExportStatusMessage } from './ExportStatusMessage';
import { useExportJob } from './useExportJob';

const COUNTRY_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Россия' },
  { value: '22', label: 'Беларусь' },
  { value: '82', label: 'Казахстан' },
];

const STATUS_LABEL: Record<number, string> = {
  [EXPORT_STATUS.PLANNED]: 'Запланировано',
  [EXPORT_STATUS.IN_PROGRESS]: 'В процессе',
  [EXPORT_STATUS.DONE]: 'Готово',
  [EXPORT_STATUS.FAILED]: 'Завершилось с ошибкой',
  [EXPORT_STATUS.CANCELLED]: 'Отменена пользователем',
};

const EMPTY = '—';

const emptyForm = {
  dateFrom: '',
  dateTo: '',
  countryId: '',
  promocodes: [] as string[],
  utmSources: [] as string[],
  billingType: '',
};

const asList = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? value : value ? [value] : [];

const formatList = (value: string | string[] | undefined) => asList(value).join(', ') || EMPTY;

const formatTime = (value: number | string | undefined) => formatUnixDateTime(value) || EMPTY;

const countryLabel = (id: number | string | undefined) =>
  COUNTRY_OPTIONS.find((option) => option.value === String(id))?.label ?? EMPTY;

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <li className="list-group-item">
    <b>{label}</b>
    <span className="pull-right">{value}</span>
  </li>
);

type Props = {
  /** Backend export family: `/export/<path>/`. */
  path: string;
  downloadFileName?: string;
  /** Country is required and sent as `country_id`. */
  hasCountrySelect?: boolean;
  /** UTM source / promo code multi-selects; at least one is required. */
  hasLoyaltyFilters?: boolean;
  /** Billing log types; when set, a required type is sent as `billing_type_id`. */
  billingTypes?: SelectOption[];
};

/** Create / cancel / download form plus the last export state; shared by every `/export/*` page. */
export const ExportPanel = ({
  path,
  downloadFileName = 'export.xlsx',
  hasCountrySelect,
  hasLoyaltyFilters,
  billingTypes,
}: Props) => {
  const [form, setForm] = useState(emptyForm);
  const [validationError, setValidationError] = useState('');

  const { info, isLoading, create, cancel, download, isBusy } = useExportJob(path, downloadFileName);

  const utmQuery = useQuery({
    queryKey: ['export-utm-sources'],
    queryFn: fetchUtmSourceOptions,
    enabled: Boolean(hasLoyaltyFilters),
  });
  const promoQuery = useQuery({
    queryKey: ['export-promo-codes'],
    queryFn: fetchPromoCodeOptions,
    enabled: Boolean(hasLoyaltyFilters),
  });

  const setField = <K extends keyof typeof emptyForm>(key: K, value: (typeof emptyForm)[K]) => {
    setValidationError('');
    create.clearErrors();
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const getValidationError = () => {
    if (!form.dateFrom) return 'Введите начальную дату';
    if (hasLoyaltyFilters && !form.promocodes.length && !form.utmSources.length) {
      return 'Выберите промокод или UTM (источник)';
    }
    if (hasCountrySelect && !form.countryId) return 'Выберите страну';
    if (billingTypes && !form.billingType) return 'Выберите тип';
    return '';
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const problem = getValidationError();
    setValidationError(problem);
    if (problem) return;

    create.save({
      from_time: dateInputToUnix(form.dateFrom) as number,
      to_time: dateInputToUnix(form.dateTo),
      country_id: hasCountrySelect ? form.countryId : undefined,
      promocode: hasLoyaltyFilters ? form.promocodes : undefined,
      utm_source: hasLoyaltyFilters ? form.utmSources : undefined,
      billing_type_id: billingTypes ? form.billingType : undefined,
    });
  };

  const onReset = () => {
    setValidationError('');
    create.clearErrors();
    setForm(emptyForm);
  };

  const serverErrors = Object.values(create.errors).flat();
  const filter = info?.filter ?? {};

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Экспорт</h3>
            <form onSubmit={onSubmit} style={{ marginTop: 12 }}>
              <FilterField label="От даты">
                <DateTimeInput
                  type="date"
                  value={form.dateFrom}
                  max={form.dateTo || undefined}
                  onChange={(v) => setField('dateFrom', v)}
                />
              </FilterField>
              <FilterField label="До даты">
                <DateTimeInput
                  type="date"
                  value={form.dateTo}
                  min={form.dateFrom || undefined}
                  onChange={(v) => setField('dateTo', v)}
                />
              </FilterField>
              {hasCountrySelect && (
                <FilterField label="Выберите страну">
                  <FilterSelect
                    options={COUNTRY_OPTIONS}
                    value={form.countryId}
                    onChange={(v) => setField('countryId', v)}
                  />
                </FilterField>
              )}
              {billingTypes && (
                <FilterField label="Тип">
                  <FilterSelect
                    options={billingTypes}
                    value={form.billingType}
                    onChange={(v) => setField('billingType', v)}
                  />
                </FilterField>
              )}
              {hasLoyaltyFilters && (
                <>
                  <FilterField label="UTM (источник)">
                    <MultiSelect
                      options={utmQuery.data ?? []}
                      value={form.utmSources}
                      isLoading={utmQuery.isLoading}
                      onChange={(v) => setField('utmSources', v)}
                    />
                  </FilterField>
                  <FilterField label="Промокод (лояльность)">
                    <MultiSelect
                      options={promoQuery.data ?? []}
                      value={form.promocodes}
                      isLoading={promoQuery.isLoading}
                      onChange={(v) => setField('promocodes', v)}
                    />
                  </FilterField>
                </>
              )}

              {info && (
                <p>
                  <ExportStatusMessage statusId={info.status_id} />
                </p>
              )}
              {info?.status_id === EXPORT_STATUS.DONE && (
                <p>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isBusy}
                    onClick={() => download.mutate()}
                  >
                    Загрузить готовый
                  </button>
                </p>
              )}

              {validationError && <p className="text-red">{validationError}</p>}
              {serverErrors.map((message) => (
                <p key={message} className="text-red">
                  {message}
                </p>
              ))}
              {(cancel.isError || download.isError) && (
                <p className="text-red">Не удалось выполнить действие с экспортом</p>
              )}

              <button type="button" className="btn btn-warning" onClick={onReset} disabled={isBusy}>
                Очистить
              </button>{' '}
              <button
                type="button"
                className="btn btn-danger"
                disabled={isBusy || !info}
                onClick={() => info && cancel.mutate(info.id)}
              >
                Отменить
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={isBusy}>
                {hasExportResult(info) ? 'Пересоздать' : 'Создать'}
              </button>
              <p style={{ marginTop: 12 }}>
                <b>При создании нового экспорта сбрасывается весь достигнутый прогресс</b>
              </p>
            </form>
          </div>
        </div>
      </div>

      <div className="col-md-6">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Данные по экспорту</h3>
            {isLoading && <p>Загрузка…</p>}
            <ul className="list-group list-group-unbordered">
              <InfoRow label="id экспорта" value={info ? String(info.id) : EMPTY} />
              <InfoRow label="Время создания выгрузки" value={formatTime(info?.create_time)} />
              {hasCountrySelect && <InfoRow label="Ограничение на страну" value={countryLabel(filter.country_id)} />}
              <InfoRow label="Начиная с даты" value={formatTime(filter.from_time)} />
              <InfoRow label="До даты" value={formatTime(filter.to_time)} />
              {hasLoyaltyFilters && (
                <>
                  <InfoRow label="UTM (источник)" value={formatList(filter.utm_source)} />
                  <InfoRow label="Промокод (лояльность)" value={formatList(filter.promocode)} />
                </>
              )}
              <InfoRow label="Статус выгрузки" value={info ? (STATUS_LABEL[info.status_id] ?? EMPTY) : EMPTY} />
              <InfoRow label="Прогресс" value={info?.progress || EMPTY} />
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
